/**
 * Helpers puros para filtrado, expansión y firmas de conductores libres.
 * Módulo puro JS sin dependencias de React Native (testeable en Node/CI).
 */

/**
 * Genera una firma string liviana basada en id, coordenadas (4 decimales) y carType
 * para detectar si la lista visual de autos en el mapa cambió realmente.
 *
 * @param {Array} list - Lista de conductores / autos
 * @returns {string} Firma representativa
 */
export const freeCarsSignature = (list) => {
    if (!list || list.length === 0) return '';
    let s = '';
    for (let i = 0; i < list.length; i++) {
        const d = list[i];
        const lat = d?.location?.lat;
        const lng = d?.location?.lng;
        s += `${d?.id}:${lat != null ? Number(lat).toFixed(4) : ''}:${lng != null ? Number(lng).toFixed(4) : ''}:${d?.carType || ''}|`;
    }
    return s;
};

/**
 * Valida si un conductor tiene una ubicación geográfica válida (números no NaN).
 *
 * @param {Object} loc - Objeto con lat y lng
 * @returns {boolean}
 */
export const hasValidDriverLocation = (loc) => {
    return !!(
        loc &&
        loc.lat !== null &&
        loc.lat !== undefined &&
        loc.lng !== null &&
        loc.lng !== undefined &&
        !isNaN(loc.lat) &&
        !isNaN(loc.lng)
    );
};

/**
 * Filtra la lista cruda de conductores activos del sistema:
 * - Excluye al propio usuario si es conductor.
 * - Excluye tipos de vehículo exclusivos (ej. women-only) si el usuario no tiene permiso.
 * - Excluye conductores encolados/ocupados (`queue === true`).
 * - Excluye conductores sin ubicación GPS válida.
 *
 * @param {Object} params
 * @param {Array} params.drivers - Lista de conductores (ej: usersdata.drivers)
 * @param {string} [params.myUid] - UID del usuario logueado
 * @param {Object} [params.profile] - Perfil del usuario actual
 * @param {Array} [params.carsRaw] - Lista de configuración de tipos de autos
 * @param {Function} [params.canCustomerSelectWomenOnly] - Validador de permisos women-only
 * @param {Function} [params.isWomenOnlyCarType] - Validador de tipo women-only
 * @returns {Array} Conductores libres y válidos
 */
export const filterFreeDrivers = ({
    drivers = [],
    myUid = null,
    profile = null,
    carsRaw = [],
    canCustomerSelectWomenOnly = null,
    isWomenOnlyCarType = null,
} = {}) => {
    if (!Array.isArray(drivers) || drivers.length === 0) return [];

    const canSeeWomenOnly = typeof canCustomerSelectWomenOnly === 'function'
        ? canCustomerSelectWomenOnly(profile)
        : true;

    return drivers.filter((d) => {
        if (!d) return false;
        if (myUid && d.id === myUid) return false;

        if (!canSeeWomenOnly && typeof isWomenOnlyCarType === 'function') {
            const ct = (carsRaw || []).find((c) => c && c.name === d.carType);
            if (ct && isWomenOnlyCarType(ct)) return false;
            if (!ct && isWomenOnlyCarType({ name: d.carType })) return false;
        }

        const isAvailable = d.queue === false || d.queue === undefined;
        const validLocation = hasValidDriverLocation(d.location);

        return isAvailable && validLocation;
    });
};

/**
 * Si un conductor libre no tiene un `carType` asignado fijo en su perfil,
 * lo expande / replica para cada tipo de auto disponible en la zona.
 *
 * @param {Array} freeDrivers - Lista ya filtrada de conductores
 * @param {Array} allCarTypes - Tipos de autos disponibles
 * @returns {Array} Lista con conductores normalizados
 */
export const expandDriversWithoutCarType = (freeDrivers = [], allCarTypes = []) => {
    if (!Array.isArray(freeDrivers) || freeDrivers.length === 0) return [];

    const result = [];
    for (let i = 0; i < freeDrivers.length; i++) {
        const driver = freeDrivers[i];
        if (!driver.carType) {
            const carTypes = allCarTypes || [];
            for (let j = 0; j < carTypes.length; j++) {
                result.push({ ...driver, carType: carTypes[j]?.name });
            }
        } else {
            result.push(driver);
        }
    }
    return result;
};

/**
 * Orquesta el filtrado y la expansión de conductores en una sola llamada pura.
 *
 * @param {Object} params
 * @returns {Array}
 */
export const processFreeDrivers = (params = {}) => {
    if (!params || !params.drivers) return [];
    const filtered = filterFreeDrivers(params);
    return expandDriversWithoutCarType(filtered, params.allCarTypes);
};
